import { useMemo, useState } from 'react';

import { format } from 'date-fns';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { FormValidatorType, createForm, useCreateFormStore } from '@components/form';
import { FormLabel } from '@components/form-label';
import { useInfoModal } from '@components/generic-modals/info-modal';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconPerson } from '@components/icons/person';
import { IconTraveller } from '@components/icons/traveller';
import { RelativeSpinner } from '@components/loading-spinner';
import { SearchBox } from '@components/search-box';
import { SelectOption } from '@components/select';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  ExtendedFieldMappingEditorValue,
  FieldMappingEditorValue,
  FieldMappingGroup,
} from '@components/text-editors/addons/field-mapping';
import { ValueList } from '@components/value-list';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { parseDate } from '@utils/date';
import { getKeys } from '@utils/objects';

import { AddToJourneyType, AttributeType, JourneyStatsType, JourneyType } from './api';
import { JourneyCard } from './components/journey-card';
import { JourneyCardList } from './components/journey-card-list';
import { JOURNEYS_LIST_CAP } from './components/journey-search';
import { JourneyListEmpty, JourneyListError, JourneyListLoading } from './components/list-states';
import { journeyListStrings } from './components/list-strings';
import { toMappedEditorValue } from './field-config/rehydrate-variables';
import { toSelectedOptions } from './field-config/serialize';
import { FieldConfigValue, ResolvedFieldEntry } from './field-config/types';
import { useBaseFieldDefaults } from './field-config/use-base-field-defaults';
import {
  excludeVariableGroupTypes,
  FILE_VARIABLE_TYPES,
  filterVariableGroupsByTypes,
} from './field-config/variable-groups';
import { useVariableGroups } from './helpers';
import {
  isJourneysTokenConnected,
  useAddToJourney,
  useCompanyTravellerAttributes,
  useJourneysStats,
  useList,
  useToken,
  useUsers,
} from './hooks';
import { AvailableJourney } from './job-triggers/api';
import { useAvailableJourneys, useJobJourneyConfig } from './job-triggers/hooks';

export const useAddToJourneySidebar = () => {
  const sidebar = useSidebar(AddToJourney, {
    variant: 'REGULAR',
    hasBackdrop: true,
  });
  return sidebar;
};

/**
 * Builds the placeholder journey a stage-entry/outcome prompt preselects the
 * sidebar with, from the trigger row's own id and title — no catalog fetch
 * needed to resolve it. `hasPublishedJourney: true` is safe: writes still
 * validate the journey against the live upstream catalog server-side.
 */
export const toPreselectedJourney = (
  initialJourneyId?: string,
  initialJourneyTitle?: string
): JourneyType | undefined =>
  initialJourneyId
    ? {
        shortId: initialJourneyId,
        title: initialJourneyTitle ?? '',
        description: '',
        hasPublishedJourney: true,
      }
    : undefined;

export const AddToJourney = ({
  applications,
  jobId,
  initialJourneyId,
  initialJourneyTitle,
}: {
  applications: number[];
  jobId: number;
  initialJourneyId?: string;
  initialJourneyTitle?: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const [journey, setJourney] = useState<JourneyType | undefined>(() =>
    toPreselectedJourney(initialJourneyId, initialJourneyTitle)
  );
  const [search, setSearch] = useState('');
  const tokenQuery = useToken();
  const isConnected = isJourneysTokenConnected(tokenQuery.data);

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('addToJourney')}
        icon={IconAddPlus}
        iconColor="blue100"
        extraHeader={
          !journey ? (
            <SearchBox onChange={setSearch} onChangeDebounce={300} placeholder={t('search')} />
          ) : undefined
        }
        hideExtraHeaderSeparator
      />
      <Sidebar.Content data-cy="add-to-journey-sidebar">
        {tokenQuery.isLoading ? (
          <RelativeSpinner />
        ) : !isConnected ? (
          <InvalidCredentials />
        ) : journey ? (
          <JourneyDetails
            journey={journey}
            clearJourney={() => setJourney(undefined)}
            applications={applications}
            jobId={jobId}
          />
        ) : (
          <SelectJourney setJourney={setJourney} search={search} jobId={jobId} />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

const InvalidCredentials = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Card space={3} variant="orange">
      <Stack space={2} orientation="horizontal" align="center">
        <IconFormIndicatorWarning />
        <Text font="regular14" color="orange200" text={t('invalidCredentialsWarning')} />
      </Stack>
    </Card>
  );
};

type SelectJourneyType = {
  setJourney: (journey: JourneyType) => void;
  search: string;
  jobId: number;
};

/** AvailableJourney (the allow-list response shape) to the JourneyType the
 * click handler and JourneyDetails need. */
const toJourneyType = (journey: AvailableJourney): JourneyType => ({
  shortId: journey.journeyId,
  title: journey.title,
  description: journey.description,
  hasPublishedJourney: true,
});

const SelectJourney = ({ setJourney, search, jobId }: SelectJourneyType) => {
  const { t } = useScopedTranslate(locale);
  const statsQuery = useJourneysStats();
  const statistics = statsQuery.data ?? [];
  const availableJourneysQuery = useAvailableJourneys(jobId, search);

  const journeys = useMemo(
    () => (availableJourneysQuery.data?.results ?? []).map(toJourneyType),
    [availableJourneysQuery.data]
  );

  function getJourneyStats(journeyId: string): JourneyStatsType | undefined {
    return statistics.find((stats) => stats.shortId === journeyId);
  }

  if (availableJourneysQuery.isLoading) {
    return <JourneyListLoading />;
  }

  // The server is the enforcement point for the allow-list: a request
  // failure fails closed with a Retry, instead of falling back to showing
  // every journey unfiltered.
  if (availableJourneysQuery.isError) {
    return (
      <JourneyListError
        description={t('loadError')}
        retryLabel={t('retry')}
        onRetry={() => availableJourneysQuery.refetch()}
      />
    );
  }

  return (
    <Stack space={2}>
      <Text font="semibold14" text={t('selectJourney')} />
      {availableJourneysQuery.data?.isTruncated && (
        <Text
          font="regular12"
          color="textHelper"
          text={t('showingFirstResults', { count: String(JOURNEYS_LIST_CAP) })}
        />
      )}
      {journeys.length === 0 ? (
        <JourneyListEmpty text={search.trim() ? t('noResults') : t('noJourneysAvailable')} />
      ) : (
        <JourneyCardList
          items={journeys}
          getKey={(journey) => journey.shortId}
          renderItem={(journey) => {
            const stats = getJourneyStats(journey.shortId);
            return (
              <JourneyCard
                title={journey.title}
                description={journey.description || t('noDescription')}
                actions={
                  stats ? (
                    <ValueList
                      items={[
                        { value: stats.numTravellers, label: t('travellers') },
                        { value: stats.numNodeActions, label: t('actions') },
                      ]}
                    />
                  ) : undefined
                }
                iconTone="blue"
                onClick={() => setJourney(journey)}
              />
            );
          }}
        />
      )}
    </Stack>
  );
};

type JourneyDetailsType = {
  journey: JourneyType;
  clearJourney: () => void;
  applications: number[];
  jobId: number;
};

type CompanyTravellerBaseFields = {
  name: FieldMappingEditorValue | string;
  email: FieldMappingEditorValue | string;
  phone: FieldMappingEditorValue | string;
};
type TravellerJourneyAttributes = {
  [K in `journey_attribute__${string}`]: FieldConfigValue;
};

type CompanyTravellerAttributes = {
  [K in `company_traveller_attribute__${string}`]: FieldConfigValue;
};
type Attributes = TravellerJourneyAttributes & CompanyTravellerAttributes;
type FormData = { startJourney: boolean } & CompanyTravellerBaseFields & Attributes;

const Form = createForm<FormData>();

/**
 * The config PUT accepts a bare string as well as the dict the editors want —
 * validate_variables_are_dicts inspects `variables` only when `value` is a
 * dict — so both shapes have to be handled rather than asserted away.
 *
 * A bare string is handed to toMappedEditorValue rather than wrapped as
 * {value}: a token with no `variables` entry is left as literal text by
 * transformInput, so wrapping alone shows the recruiter "{{ application_name }}"
 * where the pill belongs.
 */
const asEditorValue = (
  value: FieldConfigValue | undefined,
  variableGroups: FieldMappingGroup[]
) => {
  return toMappedEditorValue(value, variableGroups);
};

/**
 * Base fields (name / email / phone). A stored key is the admin's explicit
 * choice and is passed through untouched — including an empty one, which is a
 * deliberate "don't prefill this, make the recruiter type it".
 * toStoredFieldConfig only writes a base key when the draft value is not
 * undefined, so "" here can only mean a clear, never an absence.
 *
 * No coercion: the editors accept a mapping dict *or* a plain string, and
 * wrapping an empty string as {value: ''} makes them render a degenerate empty
 * pill that cannot be typed into — which would strand the recruiter on a
 * required field. Only a missing key falls back to the canonical default.
 */
const toBaseEditorValue = (
  value: FieldConfigValue | undefined,
  fallback: FieldMappingEditorValue,
  variableGroups: FieldMappingGroup[]
) => {
  if (value === undefined) return fallback;
  // An explicit clear stays the bare "" the editors treat as empty; see above.
  if (value === '') return value as unknown as FieldMappingEditorValue;
  return toMappedEditorValue(value, variableGroups) ?? fallback;
};

/**
 * Attribute fields. Unlike base fields, "" is the *absent* marker on both
 * sides — resolve_journey_fields returns "" for a field neither config supplies
 * (config_resolver.py:157) and toStoredFieldConfig writes "" for an included
 * field with no value — so it must not reach an editor as a value. A non-empty
 * string is a real configured literal and is wrapped rather than discarded.
 */
const toEditorValue = (
  value: FieldConfigValue | undefined,
  variableGroups: FieldMappingGroup[]
) => {
  return value === '' ? undefined : asEditorValue(value, variableGroups);
};

/** Same absent-vs-configured rule for the plain-string fields (date, user, select). */
const toStringValue = (value: FieldConfigValue | undefined) => {
  return typeof value === 'string' && value ? value : undefined;
};

const isNumericAttributeType = (attributeType: string) => {
  return attributeType === 'number' || attributeType === 'decimal';
};

/**
 * FormData maps every attribute key to the FieldConfigValue union, but a form
 * field's `id` has to name a key whose declared type equals that field's own
 * value type (KeysOfValueType is an exact match, not assignability). A single
 * dynamic key map cannot say "this key holds a boolean when the attribute is a
 * boolean", so the per-type fields below assert their id. The runtime binding
 * is correct — only the key-to-value-type relationship is inexpressible.
 */
const asFieldId = <TId,>(fieldId: string) => fieldId as TId;

const toBooleanValue = (value: FieldConfigValue | undefined) => {
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * Formats a date form value for submission as 'yyyy-MM-dd'. Prefilled config
 * values arrive as a bare 'yyyy-MM-dd' string (parsed as a local calendar date
 * by parseDate, not shifted to the previous day west of UTC — see
 * @utils/date's own trap warning); a touched picker replaces the value with a
 * real Date, which parseDate returns unchanged.
 */
export const toSubmittedDateValue = (value: unknown): string => {
  if (!value) return '';
  return format(parseDate(value as string | Date), 'yyyy-MM-dd');
};

/**
 * Multi-select values are stored as option short_ids — toSelectedOptions maps
 * them to SelectOption[] (same rule the config panel applies). Form.SelectMulti
 * captures its defaultValue at mount, so an empty selection must come through
 * as undefined rather than [] — a [] default would still "capture" and lock
 * the field empty even once options.length > 0 seeds a stored id.
 */
const toSelectOptions = (
  value: FieldConfigValue | undefined,
  options: SelectOption[],
  unavailableLabel: string
): SelectOption[] | undefined => {
  const selected = toSelectedOptions({ value, options, unavailableLabel });
  return selected.length > 0 ? selected : undefined;
};

/**
 * file_multi defaults are lists of mapping dicts. After Array.isArray the value
 * is still `string[] | ExtendedFieldMappingEditorValue[]`; filtering to objects
 * drops the string branch honestly instead of casting it away.
 */
export const toFileMappingList = (
  value: FieldConfigValue | undefined
): ExtendedFieldMappingEditorValue[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items = value as Array<string | ExtendedFieldMappingEditorValue>;
  const dicts = items.filter(
    (item): item is ExtendedFieldMappingEditorValue => typeof item === 'object' && item !== null
  );
  return dicts.length > 0 ? dicts : undefined;
};

const JourneyDetails = ({ journey, clearJourney, applications, jobId }: JourneyDetailsType) => {
  const { t } = useScopedTranslate(locale);
  const baseFieldDefaults = useBaseFieldDefaults();
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isStartJourneyEnabled = formValues['startJourney'];
  const showInfoModal = useInfoModal();
  const companyTravellerAttributesQuery = useCompanyTravellerAttributes();
  const companyTravellerAttributes = (companyTravellerAttributesQuery.data ?? []).filter(
    (attribute) => attribute.isActive
  );
  const fieldMappingId = `journey-${journey.shortId}`;
  const usersQuery = useUsers();
  const users = (usersQuery.data ?? []).map((user) => ({ value: user.shortId, label: user.name }));
  const handleSuccess = () => {
    if (applications.length > 1) {
      showInfoModal({
        title: t('adding'),
        children: (
          <div>
            <Text text={t('actionBeingProcessed')} />
            <Text text={t('mightTakeFewMinutes')} color="textSecondary" />
          </div>
        ),
      });
    }
    hideSidebar();
  };
  const addToJourneyMutation = useAddToJourney({ onSuccess: handleSuccess });

  const configQuery = useJobJourneyConfig(jobId, journey.shortId);

  // The config GET already fetches v1/journeys/{id}/attributes/ upstream
  // (views.JourneyTravellerAttributes hits the identical path) — reading it
  // from here instead of a second attributes fetch drops one of the two
  // attribute round-trips per sidebar open.
  const journeyTravellerAttributes = (configQuery.data?.attributes ?? []).filter(
    (attribute) => attribute.isActive
  );

  const resolvedFieldMap = useMemo<Record<string, ResolvedFieldEntry>>(() => {
    if (!configQuery.data) return {};
    return Object.fromEntries(
      configQuery.data.resolved_fields.map((entry) => [entry.short_id, entry])
    );
  }, [configQuery.data]);

  /**
   * The company-traveller tier's resolved decisions, keyed by short_id.
   *
   * Unlike resolved_fields, this list is built from the short_ids the stored
   * config mentions rather than from the whole catalog (the catalog is
   * company-wide and would cost the config endpoint another upstream call), so
   * "no entry" here means "nobody configured this field" — not the stale-id
   * grace case it means on the journey side.
   */
  const resolvedTravellerMap = useMemo<Record<string, ResolvedFieldEntry>>(() => {
    const entries = configQuery.data?.resolved_traveller_fields ?? [];
    return Object.fromEntries(entries.map((entry) => [entry.short_id, entry]));
  }, [configQuery.data]);

  // The backend merges resolved_base per key — a job override wins for the
  // keys it supplies, the rest inherit from global — so there is no precedence
  // work left to do here.
  const resolvedBase = useMemo(() => {
    if (!configQuery.data) return null;
    return configQuery.data.resolved_base ?? null;
  }, [configQuery.data]);

  // Resolved by the job config endpoint (job override > global > true). On a
  // failed config fetch keep it OFF — failing closed is right for something
  // that starts a journey on its own.
  const startJourneyDefault = configQuery.isError ? false : configQuery.data?.start_journey ?? true;

  // isInitialLoading, not isLoading: useJobJourneyConfig disables itself when
  // either id is falsy, and a disabled v4 query reports isLoading forever —
  // which would strand the sidebar on a spinner instead of degrading to the
  // unconfigured form.
  const isLoading =
    companyTravellerAttributesQuery.isLoading ||
    usersQuery.isLoading ||
    configQuery.isInitialLoading;

  const baseVariableGroups = useVariableGroups({ jobId: jobId });
  const emailVariableGroups = useVariableGroups({ jobId: jobId, types: ['email'] });
  const phoneVariableGroups = useVariableGroups({ jobId: jobId, types: ['phone'] });
  const fileVariableGroups = useVariableGroups({ jobId: jobId, types: ['file'] });
  const fileMultiVariableGroups = useVariableGroups({
    jobId: jobId,
    types: ['file_multi', 'file'],
  });
  // Text-shaped targets take almost anything, but not a file: CV and Cover
  // letter are typed `file`, and mapping one into a text field sends whatever
  // string the file resolves to rather than the file.
  const textVariableGroups = useMemo(
    () => excludeVariableGroupTypes(baseVariableGroups, FILE_VARIABLE_TYPES),
    [baseVariableGroups]
  );

  const nameDefault = toBaseEditorValue(
    resolvedBase?.name?.value,
    baseFieldDefaults.name,
    baseVariableGroups
  );
  const emailDefault = toBaseEditorValue(
    resolvedBase?.email?.value,
    baseFieldDefaults.email,
    baseVariableGroups
  );
  const phoneDefault = toBaseEditorValue(
    resolvedBase?.phone?.value,
    baseFieldDefaults.phone,
    baseVariableGroups
  );

  const handleSave = (data: FormData) => {
    if (!data) return;
    const attributes: { [key: string]: FieldConfigValue } = {};
    const keys = getKeys(data);
    for (const key of keys) {
      if (key === 'startJourney') {
        continue;
      }
      const value = data[key] ?? '';

      const attribute = key.startsWith('company_traveller_attribute__')
        ? companyTravellerAttributes.find(
            (attr) => attr.shortId === key.replace('company_traveller_attribute__', '')
          )
        : journeyTravellerAttributes.find(
            (attr) => attr.shortId === key.replace('journey_attribute__', '')
          );

      if (!attribute) {
        continue;
      }
      if (attribute.attributeType === 'date') {
        attributes[key] = toSubmittedDateValue(value);
      } else if (attribute.attributeType === 'multi_select') {
        // Form.SelectMulti holds SelectOption[]; the API takes the option ids.
        attributes[key] = Array.isArray(value)
          ? (value as SelectOption[]).map((option) => option.value)
          : [];
      } else if (isNumericAttributeType(attribute.attributeType)) {
        // These are mapping fields now, so the value is a {value, variables}
        // dict whenever a variable is in play — String() on that would send
        // "[object Object]". A dict goes through untouched, exactly as a text
        // field's does, and the backend substitutes it. Anything else is a
        // literal, which the config blob and the API both store as a string.
        attributes[key] =
          value !== null && typeof value === 'object'
            ? (value as FieldConfigValue)
            : value === '' || value === undefined
            ? ''
            : String(value);
      } else {
        attributes[key] = value;
      }
    }
    const item: AddToJourneyType = {
      journey: journey.shortId,
      applications: applications,
      name: data.name,
      email: data.email,
      phone: data.phone,
      startJourney: data.startJourney,
      attributes,
    };

    addToJourneyMutation.mutateAsync(item);
  };

  if (isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <Form store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <Stack orientation="horizontal" justify="space-between">
          <Stack space={2}>
            <Stack orientation="horizontal" space={2}>
              <IconPerson color="purple100" />
              <Text font="bold14" text={t('applicantsSelected')} />
            </Stack>
            <Stack orientation="horizontal" space={2}>
              <IconArrowRight color="blue100" />
              <Text font="bold14" text={journey.title} />
            </Stack>
          </Stack>
          <Badge text={applications.length} size="md" />
        </Stack>
        <Sidebar.Separator />
        {configQuery.isError ? (
          <JourneyListError
            dataCy="journey-config-error"
            description={t('configLoadError')}
            retryLabel={t('retry')}
            onRetry={() => configQuery.refetch()}
          />
        ) : (
          <>
            <Stack space={4}>
              <Stack space={2} orientation="horizontal">
                <IconTraveller color="purple100" />
                <Text font="bold14" text={t('journeyFields')} />
              </Stack>
              <Text
                font="regular14"
                text={styledTranslate({
                  key: 'journeyFieldsDescription',
                  variables: {
                    journeyTitle: {
                      text: `"${journey.title}"`,
                      font: 'bold14',
                      // Not the renderer's inline-block default: an atomic
                      // inline lets the line break right after it, so a long
                      // title orphaned the sentence's period onto its own
                      // line. Plain inline keeps the no-space period glued to
                      // the closing quote.
                      style: { display: 'inline' },
                    },
                  },
                  translations: locale,
                })}
              />
              <div data-testid="journey-field:Name">
                <Form.FieldMappingTextEditor
                  id="name"
                  label={t('name')}
                  validators={['required']}
                  singleLine
                  plaintext
                  variableGroups={textVariableGroups}
                  defaultValue={nameDefault}
                  fieldMappingId={fieldMappingId}
                />
              </div>
              <div data-testid="journey-field:Email">
                <Form.EmailField
                  id="email"
                  label={t('core.email')}
                  // 'email' rejects a malformed literal address without ever
                  // rejecting a field-mapped one: core's validator short-circuits
                  // on a {{ token }} before falling back to validateEmail
                  // (form/validate-form-value.ts). A traveller is created from
                  // this field, so a typo here reaches the Journeys API.
                  validators={['required', 'email']}
                  variableGroups={emailVariableGroups}
                  defaultValue={emailDefault}
                  fieldMappingId={fieldMappingId}
                />
              </div>
              <div data-testid="journey-field:Phone">
                <Form.FieldMappingPhoneField
                  id="phone"
                  label={t('phone')}
                  variableGroups={phoneVariableGroups}
                  defaultValue={phoneDefault}
                  fieldMappingId={fieldMappingId}
                  fieldMapText={{
                    title: t('fieldMapTitle'),
                    description: t('fieldMapDescription'),
                  }}
                />
              </div>
              {companyTravellerAttributes
                .filter((attribute) => {
                  // Not opted in, not shown — the config is authoritative even
                  // when it is empty, which is what makes removing a field work
                  // and is how journey attributes already behave. Showing all of
                  // them while the tier was unconfigured read as "removing does
                  // nothing" instead.
                  const entry = resolvedTravellerMap[attribute.shortId];
                  return Boolean(entry && (entry.required || entry.included));
                })
                .map((attribute) => (
                  <div key={attribute.shortId} data-testid={`journey-field:${attribute.title}`}>
                    <AttributeField
                      attribute={attribute}
                      isCompanyTravellerAttribute
                      users={users}
                      baseVariableGroups={baseVariableGroups}
                      emailVariableGroups={emailVariableGroups}
                      phoneVariableGroups={phoneVariableGroups}
                      fileVariableGroups={fileVariableGroups}
                      fileMultiVariableGroups={fileMultiVariableGroups}
                      fieldMappingId={fieldMappingId}
                      resolvedEntry={resolvedTravellerMap[attribute.shortId]}
                    />
                  </div>
                ))}
            </Stack>
            {journeyTravellerAttributes.length > 0 && (
              <>
                <Sidebar.Separator />
                <Stack space={4}>
                  {journeyTravellerAttributes
                    .filter((attribute) => {
                      // resolved_fields covers every live journey attribute, so a
                      // missing entry here is a stale id, not an unconfigured
                      // field — hence shown. The traveller tier above reads the
                      // opposite way; see resolvedTravellerMap.
                      const entry = resolvedFieldMap[attribute.shortId];
                      if (!entry) return true;
                      return entry.is_required || entry.required || entry.included;
                    })
                    .map((attribute) => {
                      const resolvedEntry = resolvedFieldMap[attribute.shortId];
                      return (
                        <div
                          key={attribute.shortId}
                          data-testid={`journey-field:${attribute.title}`}
                        >
                          <AttributeField
                            attribute={attribute}
                            users={users}
                            baseVariableGroups={baseVariableGroups}
                            emailVariableGroups={emailVariableGroups}
                            phoneVariableGroups={phoneVariableGroups}
                            fileVariableGroups={fileVariableGroups}
                            fileMultiVariableGroups={fileMultiVariableGroups}
                            fieldMappingId={fieldMappingId}
                            resolvedEntry={resolvedEntry}
                          />
                        </div>
                      );
                    })}
                </Stack>
              </>
            )}
            <Sidebar.Separator />
            <Stack orientation="horizontal" justify="space-between">
              <Stack>
                <FormLabel text={t('startJourneyWhenAdded')} />
                {isStartJourneyEnabled ? (
                  <Text text={t('core.yes')} />
                ) : (
                  <Text color="textHelper" text={t('core.no')} />
                )}
              </Stack>
              <Form.Switch
                id="startJourney"
                label={t('startJourneyWhenAdded')}
                labelVisible={false}
                defaultValue={startJourneyDefault}
              />
            </Stack>
          </>
        )}
        <Sidebar.Footer>
          <Stack orientation="horizontal" justify="space-between">
            <Button
              variant="secondary"
              iconLeft={<IconArrowLeft />}
              text={t('core.back')}
              onClick={clearJourney}
            />
            <ButtonGroup align="right">
              <Button variant="secondary" text={t('core.cancel')} onClick={hideSidebar} />
              <Form.Submit
                variant="primary-blue"
                text={t('addToJourney')}
                disabled={addToJourneyMutation.isLoading || configQuery.isError}
              />
            </ButtonGroup>
          </Stack>
        </Sidebar.Footer>
      </Stack>
    </Form>
  );
};

type CommonFieldProps = {
  id: keyof Attributes;
  label: string;
  validators?: FormValidatorType[];
  fieldMappingId: string;
};

const AttributeField = ({
  attribute,
  isCompanyTravellerAttribute = false,
  users,
  baseVariableGroups,
  emailVariableGroups,
  phoneVariableGroups,
  fileVariableGroups,
  fileMultiVariableGroups,
  fieldMappingId,
  resolvedEntry,
}: {
  attribute: AttributeType;
  isCompanyTravellerAttribute?: boolean;
  users: SelectOption[];
  baseVariableGroups: FieldMappingGroup[];
  emailVariableGroups: FieldMappingGroup[];
  phoneVariableGroups: FieldMappingGroup[];
  fileVariableGroups: FieldMappingGroup[];
  fileMultiVariableGroups: FieldMappingGroup[];
  fieldMappingId: CommonFieldProps['fieldMappingId'];
  resolvedEntry?: ResolvedFieldEntry;
}) => {
  const { t } = useScopedTranslate(locale);
  const listQuery = useList(attribute.options ?? '');

  const fieldId: keyof Attributes = isCompanyTravellerAttribute
    ? `company_traveller_attribute__${attribute.shortId}`
    : `journey_attribute__${attribute.shortId}`;

  // resolvedEntry.required is the Hire-side Required/Optional flag, and it is
  // false both when an admin marked the field optional and when nothing set the
  // key at all — configs saved before the flag existed have no `required` key,
  // and the resolver does not fall back to the attribute's own isRequired.
  // required_source separates the two: an explicit choice wins in both
  // directions, an absent one leaves the journey attribute's requirement standing.
  const isRequired = resolvedEntry?.required_source ? resolvedEntry.required : attribute.isRequired;

  const commonFieldProps: CommonFieldProps = {
    id: fieldId,
    label: attribute.title,
    validators: isRequired ? ['required'] : [],
    fieldMappingId: fieldMappingId,
  };

  const defaultValue = resolvedEntry?.value;
  const listOptions: SelectOption[] = (listQuery.data?.options ?? []).map((option) => ({
    value: option.shortId,
    label: option.label,
  }));

  // The picker for a text-shaped target hides file variables; rehydration below
  // still reads the unfiltered list, because a token already in the stored value
  // has to resolve whatever the picker is willing to offer today.
  const textVariableGroups = excludeVariableGroupTypes(baseVariableGroups, FILE_VARIABLE_TYPES);

  switch (attribute.attributeType) {
    case 'text':
      return (
        <Form.FieldMappingTextEditor
          {...commonFieldProps}
          singleLine
          plaintext
          variableGroups={textVariableGroups}
          defaultValue={toEditorValue(defaultValue, baseVariableGroups)}
        />
      );
    case 'paragraph':
      return (
        <Form.FieldMappingTextEditor
          {...commonFieldProps}
          variableGroups={textVariableGroups}
          defaultValue={toEditorValue(defaultValue, baseVariableGroups)}
        />
      );
    case 'date':
      return (
        <Form.DatePicker
          {...commonFieldProps}
          placeholder={t('dateFormat')}
          defaultValue={toStringValue(defaultValue)}
        />
      );
    case 'user':
      return (
        <Form.Select
          {...commonFieldProps}
          options={users}
          defaultValue={toStringValue(defaultValue)}
        />
      );
    case 'phone_number':
      return (
        <Form.FieldMappingPhoneField
          {...commonFieldProps}
          variableGroups={phoneVariableGroups}
          defaultValue={toEditorValue(defaultValue, baseVariableGroups)}
          fieldMapText={{
            title: t('fieldMapTitle'),
            description: t('fieldMapDescription'),
          }}
        />
      );
    case 'email':
      return (
        <Form.EmailField
          {...commonFieldProps}
          // Appended rather than replacing: the attribute may also be required.
          // Mirrors the Journeys twin of this switch (ui/journeys/src/ui/
          // actions/common.tsx), and is mustache-safe — see the base email field.
          validators={[...(commonFieldProps.validators ?? []), 'email']}
          variableGroups={emailVariableGroups}
          defaultValue={toEditorValue(defaultValue, baseVariableGroups)}
        />
      );
    case 'single_select':
      return (
        <Form.Select
          {...commonFieldProps}
          options={listOptions}
          defaultValue={toStringValue(defaultValue)}
        />
      );
    case 'multi_select':
      // Unlike Form.Select, whose defaultValue is a raw id resolved against the
      // live options prop, Form.SelectMulti's defaultValue carries the labels
      // and is captured at mount. Rendering before the option list resolves
      // would freeze every stored id as "Unavailable option", so wait for it.
      if (listQuery.isInitialLoading) {
        // Form.SelectMulti captures its defaultValue at mount, so it cannot render
        // before the options resolve. Hold the slot with a disabled twin instead of
        // returning null, which silently changed the form's height.
        //
        // key="loading" (paired with key="loaded" below) forces React to
        // unmount this placeholder rather than reuse it once options
        // resolve. Without distinct keys, React treats both branches as the
        // same element and only updates its props — but useFormField's
        // underlying form store registers a field's defaultValue once and
        // keeps it, so the placeholder's empty defaultValue would win
        // permanently and the resolved one below would never take effect.
        return (
          <Form.SelectMulti
            {...commonFieldProps}
            key="loading"
            id={asFieldId(fieldId)}
            options={[]}
            isDisabled
            placeholder={t('loadingOptions')}
          />
        );
      }
      return (
        <Form.SelectMulti
          {...commonFieldProps}
          key="loaded"
          id={asFieldId(fieldId)}
          options={listOptions}
          defaultValue={toSelectOptions(defaultValue, listOptions, t('unavailableOption'))}
        />
      );
    case 'number':
    case 'decimal': {
      const isDecimal = attribute.attributeType === 'decimal';
      return (
        <Form.FieldMappingNumberField
          {...commonFieldProps}
          isDecimal={isDecimal}
          // A decimal target accepts a whole number too; an integer target must
          // not be offered a decimal-typed variable.
          variableGroups={filterVariableGroupsByTypes(
            baseVariableGroups,
            isDecimal ? ['number', 'decimal'] : ['number']
          )}
          defaultValue={toEditorValue(defaultValue, baseVariableGroups)}
        />
      );
    }
    case 'boolean':
      return (
        <Stack orientation="horizontal" justify="space-between" align="center">
          <FormLabel text={attribute.title} />
          <Form.Switch
            {...commonFieldProps}
            id={asFieldId(fieldId)}
            label={attribute.title}
            labelVisible={false}
            defaultValue={toBooleanValue(defaultValue) ?? false}
          />
        </Stack>
      );
    case 'file':
      return (
        <Form.FieldMappingFileField
          {...commonFieldProps}
          variableGroups={fileVariableGroups}
          allowUploadFiles={false}
          defaultValue={toEditorValue(defaultValue, baseVariableGroups)}
        />
      );
    case 'file_multi':
      return (
        <Form.FieldMappingFileListField
          {...commonFieldProps}
          variableGroups={fileMultiVariableGroups}
          allowUploadFiles={false}
          defaultValue={toFileMappingList(defaultValue)}
        />
      );
    default:
      return null;
  }
};

const locale = {
  en: {
    ...journeyListStrings.en,
    addToJourney: 'Add to journey',
    selectJourney: 'Select journey',
    noDescription: 'No description',
    noJourneysAvailable: 'No journeys available',
    configLoadError: 'Failed to load the journey configuration. Please try again.',
    retry: 'Retry',
    unavailableOption: 'Unavailable option',
    loadingOptions: 'Loading options…',
    dateFormat: 'DD / MM / YYYY',
    travellers: 'Travellers',
    actions: 'Actions',
    applicantsSelected: 'Applicant/s Selected',
    journeyFields: 'Journey fields',
    journeyFieldsDescription:
      'The fields below are used in the journey {journeyTitle}. Fill out the relevant fields before adding your applicants to the journey.',
    name: 'Name',
    phone: 'Phone',
    search: 'Search',
    adding: 'Adding',
    actionBeingProcessed: 'This action is being processed',
    mightTakeFewMinutes: 'Please note this might take a few minutes',
    invalidCredentialsWarning:
      'Your credentials have either expired or been removed, please reconnect to re-enable this integration',
    fieldMapTitle: 'Field map',
    fieldMapDescription: 'Receive value from a field in the application',
    startJourneyWhenAdded: 'Start journey when added',
  },
  is: {
    ...journeyListStrings.is,
    addToJourney: 'Senda í journey',
    selectJourney: 'Velja journey',
    noDescription: 'Engin lýsing',
    noJourneysAvailable: 'Engin Journey-ferli í boði',
    configLoadError: 'Ekki tókst að sækja stillingar Journey-ferlisins. Reyndu aftur.',
    retry: 'Reyna aftur',
    unavailableOption: 'Valkostur ekki í boði',
    loadingOptions: 'Sæki valkosti…',
    dateFormat: 'DD / MM / ÁÁÁÁ',
    travellers: 'Travellers',
    actions: 'Actions',
    applicantsSelected: 'Valdnir umsækjendur',
    journeyFields: 'Journey upplýsingar',
    journeyFieldsDescription:
      'Upplýsingarnar hér fyrir neðan eru notaðar í {journeyTitle}. Fylltu út viðeigandi reiti áður en þú sendir umsækjendur í valið journey',
    name: 'Nafn',
    phone: 'Sími',

    search: 'Leita',
    adding: 'Bæti við',
    actionBeingProcessed: 'Þessi aðgerð er í vinnslu',
    mightTakeFewMinutes: 'Vinsamlegast athugaðu að þetta gæti tekið nokkrar mínútur',
    invalidCredentialsWarning:
      'Þínar aðgangssupplýsingar eru útrunnar eða hafa verið fjarlægðar, vinsamlegast setjið upp tenginguna aftur',
    fieldMapTitle: 'Ráðningargögn',
    fieldMapDescription: 'Nota gildi úr ráðningargögnum',
    startJourneyWhenAdded: 'Hefja journey sjálfvirkt',
  },
};
